import { Suspense, useState, useLayoutEffect, lazy } from 'react';
import { Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { RoutePath, GuardedRoute, history, routeAccess } from 'src/router';

import store, { useSelector } from 'src/store';
import { authRoleSelector, isLoggedInSelector } from 'src/store/selectors/authSelector';

import usePreviousLocationInStore from 'src/hooks/usePreviousLocationInStore';

import { PageLoading } from '@itm/shared-frontend/lib/components';

import AuthProvider from 'src/hocs/AuthProvider';
import SyncFiltersWithURL from 'src/hocs/SyncFiltersWithURL';
import Layout from 'src/components/layout/Layout';
import BreadcrumbsTrail from 'src/components/BreadcrumbsTrail';
import DirectDebitMappingForManagers from 'src/pages/configuration/components/payroll/DirectDebitMappingForManagers';

import LoginRedirectPage from 'src/pages/loginRedirect';
import PermissionDeniedPage from 'src/pages/permissionDenied';

const DataManagementRoutes = lazy(() => import('src/pages/dataManagement'));
const ReportingRoutes = lazy(() => import('src/pages/reporting'));
const ConfigurationRoutes = lazy(() => import('src/pages/configuration'));
const ProductInfoPage = lazy(() => import('src/pages/productInfo/ProductInfo'));

import { Role } from 'src/types';

function RootRoutesFallback() {
  return (
    <div className="px-3">
      <div className="container">
        <PageLoading />
      </div>
    </div>
  );
}

function RootRoutes() {
  const userRole = useSelector(authRoleSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  usePreviousLocationInStore();

  return (
    <Layout>
      <BreadcrumbsTrail />
      {userRole === Role.Manager && <DirectDebitMappingForManagers />}

      <Suspense fallback={<RootRoutesFallback />}>
        <Routes>
          <Route
            index
            element={
              <Navigate to={isLoggedIn ? RoutePath.dataManagementRoot : RoutePath.loginRedirect} replace={true} />
            }
          />

          <Route path={RoutePath.loginRedirect} element={<LoginRedirectPage />} />
          <Route path={RoutePath.permissionDenied} element={<PermissionDeniedPage />} />

          <Route
            path={`${RoutePath.configurationRoot}/*`}
            element={
              <GuardedRoute meta={{ auth: true, role: { allowList: routeAccess.configurationRoot } }}>
                <ConfigurationRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={`${RoutePath.dataManagementRoot}/*`}
            element={
              <GuardedRoute meta={{ auth: true, role: { allowList: routeAccess.dataManagementRoot } }}>
                <DataManagementRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={`${RoutePath.reportingRoot}/*`}
            element={
              <GuardedRoute meta={{ auth: true, role: { allowList: routeAccess.reportingRoot } }}>
                <ReportingRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={RoutePath.productInfo}
            element={
              <GuardedRoute meta={{ auth: true, role: { allowList: routeAccess.productInfo } }}>
                <ProductInfoPage />
              </GuardedRoute>
            }
          />

          <Route path="*" element={<Navigate to={RoutePath.root} />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function RootRouter() {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });
  useLayoutEffect(() => history.listen(setState), []);
  return (
    <Router location={state.location} navigationType={state.action} navigator={history}>
      <Provider store={store}>
        <AuthProvider>
          <SyncFiltersWithURL>
            <RootRoutes />
          </SyncFiltersWithURL>
        </AuthProvider>
      </Provider>
    </Router>
  );
}

export default RootRouter;
